import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { http, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { catLabel } from "@/components/common";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Loader2 } from "lucide-react";

const TYPES = ["product", "material", "glass", "opening", "color", "accessory", "service"];

export default function CatalogoTab({ admin }) {
  const [type, setType] = useState("product");
  const [items, setItems] = useState(null);
  const [newLabel, setNewLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const { t } = useTranslation();

  const load = useCallback(() => {
    setItems(null);
    http.get(`/catalog?type=${type}`).then((r) => setItems(r.data)).catch((e) => { setItems([]); toast.error(apiErr(e)); });
  }, [type]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (it, active) => {
    if (!admin) return;
    try {
      if (it.is_custom) await http.put(`/catalog/custom/${it.id}`, { active });
      else await http.post("/catalog/toggle", { type, master_key: it.master_key, active });
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, active } : x)));
    } catch (e) { toast.error(apiErr(e)); load(); }
  };

  const addCustom = async () => {
    if (!newLabel.trim()) return;
    setBusy(true);
    try { await http.post("/catalog/custom", { type, label: newLabel.trim() }); setNewLabel(""); toast.success(t("catalog.saved")); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const duplicate = async (it) => { try { await http.post(`/catalog/custom/${it.id}/duplicate`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const del = async (it) => {
    try { await http.delete(`/catalog/custom/${it.id}`); toast.success(t("catalog.deleted")); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="catalog-tab">
      <div className="flex flex-wrap gap-2">
        {TYPES.map((tk) => (
          <button key={tk} data-testid={`catalog-type-${tk}`} onClick={() => setType(tk)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${type === tk ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
            {t(`catalog.${tk}`)}
          </button>
        ))}
      </div>

      {admin && (
        <div className="flex gap-2">
          <Input data-testid="catalog-new-label" placeholder={`${t("catalog.add_custom")} (${t(`catalog.${type}`)})`} value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCustom()} className="max-w-sm" />
          <Button data-testid="catalog-add-btn" onClick={addCustom} disabled={busy || !newLabel.trim()} className="bg-[#0F172A] gap-1.5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {t("common.add")}
          </Button>
        </div>
      )}

      {items === null ? (
        <div className="text-sm text-slate-400 py-6">{t("common.loading")}</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-2" data-testid="catalog-list">
          {items.map((it) => (
            <Card key={it.id} data-testid={`catalog-item-${it.key}`} className="p-3 flex items-center justify-between border-slate-200">
              <div className="min-w-0">
                <div className="font-medium text-[#0F172A] text-sm flex items-center gap-2">
                  {catLabel(t, it)}
                  {it.is_custom && <span className="text-[9px] uppercase bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded">{t("catalog.custom_badge")}</span>}
                </div>
                {it.code ? <div className="text-[11px] text-slate-400">{it.code}</div> : null}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {it.is_custom && admin && (
                  <>
                    <button data-testid={`catalog-dup-${it.key}`} onClick={() => duplicate(it)} className="text-slate-400 hover:text-slate-600"><Copy className="w-4 h-4" /></button>
                    <button data-testid={`catalog-del-${it.key}`} onClick={() => del(it)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </>
                )}
                <Switch data-testid={`catalog-switch-${it.key}`} checked={it.active} disabled={!admin} onCheckedChange={(v) => toggle(it, v)} />
              </div>
            </Card>
          ))}
          {items.length === 0 && <div className="text-sm text-slate-400">{t("catalog.empty", "Nessuna voce.")}</div>}
        </div>
      )}
      {!admin && <p className="text-xs text-slate-400">{t("catalog.readonly")}</p>}
    </div>
  );
}
